import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, matchPath, useLocation } from "react-router-dom";
import { AuthProvider } from "@/components/providers/AuthProvider";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Suspense, lazy, useEffect, useState } from "react";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { useIsPwa } from "@/hooks/use-is-pwa";
import { IOSInstallPrompt } from "@/components/pwa/IOSInstallPrompt";
import { AppLoadingScreen } from "@/components/shared/AppLoadingScreen";
import { BackgroundUploadManager } from "@/components/providers/BackgroundUploadManager";
import { getOfflinePdfEntry, listOfflinePdfEntries } from "@/hooks/use-offline-pdf-cache";
import { getAppBasePath } from "@/lib/site-url";

import { WebMCPProvider } from "@/components/layout/WebMCPProvider";

/**
 * Safety net: clears any stale reader-mode scroll lock whenever the route changes.
 * If `exitReaderMode` fails to run during navigation (e.g. due to a React lifecycle race),
 * body/root can end up stuck with overflow:hidden. This guard catches that state.
 */
function ScrollLockGuard() {
  const location = useLocation();
  useEffect(() => {
    const body = document.body;
    const root = document.getElementById("root");
    if (!body.classList.contains("reader-mode")) {
      // Not in reader mode — clear any stale overflow lock
      if (body.style.overflow === "hidden") body.style.overflow = "";
      if (root && root.style.overflow === "hidden") root.style.overflow = "";
    }
  }, [location.pathname]);
  return null;
}

// Lazy Import Pages
const Index = lazy(() => import("./pages/public/Index"));
const Login = lazy(() => import("./pages/auth/Login"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const UpdatePassword = lazy(() => import("./pages/auth/UpdatePassword"));
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmail"));
const AuthCallback = lazy(() => import("./pages/auth/AuthCallback"));
const PostAuthRedirect = lazy(() => import("./pages/auth/PostAuthRedirect"));
const Onboarding = lazy(() => import("./pages/auth/Onboarding"));
const Library = lazy(() => import("./pages/student/Library"));
const LibraryProfile = lazy(() => import("./pages/student/LibraryProfile"));
const OfflineLibrary = lazy(() => import("./pages/student/OfflineLibrary"));
const StudentDashboard = lazy(() => import("./pages/student/StudentDashboard"));
const StudyTimePage = lazy(() => import("./pages/student/StudyTime"));
const StudentInboxPage = lazy(() => import("./pages/student/Inbox"));
const MyOrders = lazy(() => import("./pages/student/MyOrders"));
const PaymentRequestPage = lazy(() => import("./pages/student/PaymentRequest"));
const StudentMyLibrary = lazy(() => import("./pages/student/MyLibrary"));
const Profile = lazy(() => import("./pages/student/Profile"));
const NotificationsPage = lazy(() => import("./pages/student/Notifications"));
const Review = lazy(() => import("./pages/student/Review"));
const AiPlan = lazy(() => import("./pages/student/AiPlan"));
const ReportPage = lazy(() => import("./pages/shared/Report"));
const NotFound = lazy(() => import("./pages/public/NotFound"));

const ResourceDetail = lazy(() => import("./pages/student/ResourceDetail"));
const Contact = lazy(() => import("./pages/public/Contact"));
const Features = lazy(() => import("./pages/public/Features"));
const Toofi = lazy(() => import("./pages/public/Toofi"));
const OfflineStudy = lazy(() => import("./pages/public/OfflineStudy"));
const TrustedResources = lazy(() => import("./pages/public/TrustedResources"));
const ForPublishers = lazy(() => import("./pages/public/ForPublishers"));
const StudyPlanShare = lazy(() => import("./pages/public/StudyPlanShare"));
const HowToPay = lazy(() => import("./pages/public/HowToPay"));
const Copyright = lazy(() => import("./pages/public/Copyright"));
const Privacy = lazy(() => import("./pages/public/Privacy"));
const Terms = lazy(() => import("./pages/public/Terms"));
const RefundPolicy = lazy(() => import("./pages/public/RefundPolicy"));
const PublisherAgreement = lazy(() => import("./pages/public/PublisherAgreement"));
const About = lazy(() => import("./pages/public/About"));
const Pricing = lazy(() => import("./pages/public/Pricing"));
const InstallGuide = lazy(() => import("./pages/public/InstallGuide"));

const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminInboxPage = lazy(() => import("./pages/admin/AdminInbox"));
const StudentAnalyticsPage = lazy(() => import("./pages/admin/StudentAnalytics"));
const FeatureFlagsPage = lazy(() => import("./pages/admin/FeatureFlags"));
const AdminResources = lazy(() => import("./pages/admin/Resources"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminPlans = lazy(() => import("./pages/admin/Plans"));
const AdminBundleSales = lazy(() => import("./pages/admin/BundleSales"));
const AdminMessages = lazy(() => import("./pages/admin/Messages"));
const AdminReports = lazy(() => import("./pages/admin/Reports"));
const AdminLogs = lazy(() => import("./pages/admin/Logs"));
const AdminAnnouncements = lazy(() => import("./pages/admin/Announcements"));
const AdminStatistics = lazy(() => import("./pages/admin/AdminStatistics"));

const AdminSubmissionsPage = lazy(() => import("./pages/admin/Submissions"));
const AdminPlanOrders = lazy(() => import("./pages/admin/PlanOrders"));
const UpdateManager = import.meta.env.PROD
  ? lazy(() => import("./components/pwa/UpdateManager").then((mod) => ({ default: mod.UpdateManager })))
  : null;
const MobileImmersiveIntro = lazy(() => import("./components/auth/MobileImmersiveIntro").then(mod => ({ default: mod.MobileImmersiveIntro })));

const preloadCoreStudentRoutes = () => {
  void import("./pages/student/Library");
  void import("./pages/student/OfflineLibrary");
  void import("./pages/student/StudentDashboard");
};


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Prevents "reloading" feel when switching tabs
      staleTime: 1000 * 60 * 5,    // Keep data fresh for 5 minutes during inactivity
    },
  },
});


// ADMIN ROUTE COMPONENT (Restrict to admins only)
function AdminRoute({ children }: { children: JSX.Element }) {
  const { user, isLoading, isAdmin } = useAuth();

  if (isLoading) return <AppLoadingScreen message="Preparing your dashboard..." />;

  // 1. Not logged in -> Auth
  if (!user) return <Navigate to="/auth" />;

  // 2. Not an Admin -> Redirect to Home
  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
}

// PUBLISHER (LIBRARY OWNER) ROUTE COMPONENT
function PublisherRoute({ children }: { children: JSX.Element }) {
  const { user, profile, isLoading, isLibraryOwner, isAdmin, library } = useAuth();
  const publisherStatus =
    (profile as { publisher_status?: "pending" | "approved" | "rejected" | null } | null)?.publisher_status
    || (library?.status === "verified" ? "approved" : library?.status || null)
    || (isLibraryOwner ? "pending" : null);

  if (isLoading) return <AppLoadingScreen message="Preparing your dashboard..." />;

  if (!user) return <Navigate to="/auth" />;

  // Allow both Library Owners AND Admins to access these pages
  if (!isLibraryOwner && !isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  // Require onboarding before entering publisher tools.
  if (!profile?.onboarding_completed && !isAdmin) {
    return <Navigate to="/onboarding" replace />;
  }

  // STRICT APPROVAL CHECK (Admins bypass)
  if (!isAdmin) {
    if (publisherStatus === "pending") {
      return <Navigate to="/publisher/waiting" />;
    }
    if (publisherStatus === "rejected") {
      return <Navigate to="/publisher/rejected" />;
    }
  }

  return children;
}

const PublisherDashboard = lazy(() => import("./pages/publisher/PublisherDashboard"));
const MyLibraryPage = lazy(() => import("./pages/publisher/MyLibrary"));
const SubmissionsPage = lazy(() => import("./pages/publisher/Submissions"));
const AccessRequestsPage = lazy(() => import("./pages/publisher/AccessRequests"));
const PublisherProfile = lazy(() => import("./pages/publisher/PublisherProfile"));
const WaitingForApproval = lazy(() => import("./pages/publisher/WaitingForApproval"));
const AccessDenied = lazy(() => import("./pages/publisher/AccessDenied"));
const PublisherStatistics = lazy(() => import("./pages/publisher/PublisherStatistics"));

// STUDENT ROUTE COMPONENT (Restrict to Students)
function StudentRoute({ children }: { children: JSX.Element }) {
  const { user, isLoading, isLibraryOwner, isAdmin, needsOnboarding } = useAuth();
  const location = useLocation();

  if (isLoading) return <AppLoadingScreen message="Preparing your dashboard..." />;

  // 1. Not logged in -> Auth
  if (!user) return <Navigate to="/auth" />;

  // 2. Not onboarded -> Onboarding
  if (needsOnboarding) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/onboarding?next=${encodeURIComponent(next)}`} replace />;
  }

  // 3. Redirect Library Owners to Publisher Dashboard.
  // They should not have access to the Student Dashboard or general Student Profile.
  if (isLibraryOwner && !isAdmin) {
    return <Navigate to="/publisher" replace />;
  }

  return children;
}

// PROTECTED ROUTE COMPONENT (Base Auth Check)
function ProtectedRoute({ children }: { children: JSX.Element }) {
  const { user, isLoading, needsOnboarding } = useAuth();
  const location = useLocation();

  const allowOfflineLocalContent = (() => {
    if (typeof navigator === "undefined" || navigator.onLine) return false;

    if (location.pathname === "/library" || location.pathname === "/library/offline") {
      return true;
    }

    const resourceMatch = matchPath("/resource/:id", location.pathname);
    if (resourceMatch?.params.id) {
      return Boolean(getOfflinePdfEntry(resourceMatch.params.id));
    }

    if (location.pathname.startsWith("/library/")) {
      return listOfflinePdfEntries().length > 0;
    }

    return false;
  })();

  if (isLoading) return <AppLoadingScreen message="Preparing your dashboard..." />;

  if (allowOfflineLocalContent) return children;

  // 1. Not logged in -> Auth
  if (!user) return <Navigate to="/auth" />;

  // 2. Not onboarded -> Onboarding
  if (needsOnboarding) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/onboarding?next=${encodeURIComponent(next)}`} replace />;
  }

  return children;
}

function FeatureAvailabilityRoute({
  flagId,
  fallbackPath,
  children,
}: {
  flagId: string;
  fallbackPath: string;
  children: JSX.Element;
}) {
  const enabled = useFeatureFlag(flagId);

  if (!enabled) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
}



function MobileIntroTrigger() {
  const [showMobileIntro, setShowMobileIntro] = useState(false);
  const { user, needsOnboarding, isLoading: authLoading } = useAuth();

  const location = useLocation();

  useEffect(() => {
    if (authLoading) return;
    
    const isMobile = window.innerWidth < 768;
    const hasSeenIntro = localStorage.getItem("athr.mobile-intro-seen");
    
    const searchParams = new URLSearchParams(window.location.search);
    const forceIntro = searchParams.get("force_intro") === "true";
    
    const lastSeen = hasSeenIntro ? parseInt(hasSeenIntro, 10) : 0;
    const isStale = Date.now() - lastSeen > 1000 * 60 * 60 * 24;

    const shouldShow = isMobile && (isStale || forceIntro) && (!user || needsOnboarding);
    
    // Debug log for the user
    if (isMobile && !shouldShow) {
      console.log("[ATHR] Mobile intro skipped:", { 
        isStale, 
        forceIntro, 
        hasUser: !!user, 
        needsOnboarding,
        lastSeen: hasSeenIntro ? new Date(parseInt(hasSeenIntro)).toLocaleString() : 'never'
      });
    }

    if (shouldShow) {
      setShowMobileIntro(true);
    }
  }, [user, needsOnboarding, authLoading, location.pathname, location.search]);

  const handleIntroDone = () => {
    localStorage.setItem("athr.mobile-intro-seen", Date.now().toString());
    setShowMobileIntro(false);
  };

  if (!showMobileIntro) return null;

  return (
    <Suspense fallback={null}>
      <MobileImmersiveIntro onDone={handleIntroDone} />
    </Suspense>
  );
}

function App() {
  const isPwa = useIsPwa();
  const appBasePath = getAppBasePath();

  // FORCE LIGHT MODE: Always remove 'dark' class and lock color scheme
  useEffect(() => {
    document.documentElement.classList.remove("dark");
    document.body.classList.remove("dark");
    document.documentElement.style.colorScheme = "light";
    document.body.style.colorScheme = "light";
  }, []);

  useEffect(() => {
    document.body.classList.toggle("is-pwa", isPwa);
    document.body.classList.toggle("is-browser", !isPwa);
  }, [isPwa]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    if (idleWindow.requestIdleCallback) {
      const handle = idleWindow.requestIdleCallback(() => {
        preloadCoreStudentRoutes();
      }, { timeout: 1500 });

      return () => {
        idleWindow.cancelIdleCallback?.(handle);
      };
    }

    const timeout = window.setTimeout(() => {
      preloadCoreStudentRoutes();
    }, 350);

    return () => {
      window.clearTimeout(timeout);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <IOSInstallPrompt />
      {UpdateManager && (
        <Suspense fallback={null}>
          <UpdateManager />
        </Suspense>
      )}
      <AuthProvider>
        <BackgroundUploadManager />
        <BrowserRouter basename={appBasePath || undefined}>
          <WebMCPProvider>
            <MobileIntroTrigger />
            <ScrollLockGuard />
            <ErrorBoundary>
              <Suspense fallback={
                <AppLoadingScreen message="Preparing your dashboard..." />
              }>
                <Routes>
                {/* --- GROUP 1: STANDALONE PAGES (Full Screen, No Layout) --- */}
                {/* Auth is standalone */}
                <Route path="/auth/login" element={<Login />} />
                <Route path="/auth/signup" element={<Signup />} />
                <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                <Route path="/auth/update-password" element={<UpdatePassword />} />
                <Route path="/auth/reset-password" element={<Navigate to="/auth/update-password" replace />} />
                <Route path="/auth/verify-email" element={<VerifyEmail />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/auth/continue" element={<PostAuthRedirect />} />
                <Route path="/onboarding" element={<Onboarding />} />

                  {/* Publisher Status Pages (Standalone) */}
                  <Route path="/publisher/waiting" element={<WaitingForApproval />} />
                  <Route path="/publisher/rejected" element={<AccessDenied />} />

                  {/* Fallback for old /auth route */}
                  <Route path="/auth" element={<Navigate to="/auth/login" replace />} />

                  {/* --- GROUP 2: MAIN APP (DashboardLayout with Header) --- */}
                  <Route element={<DashboardLayout />}>
                    {/* Landing page - PUBLIC (redirects logged-in users to dashboard internally) */}
                    <Route path="/" element={<Index />} />

                    <Route path="/dashboard" element={
                      <StudentRoute>
                        <StudentDashboard />
                      </StudentRoute>
                    } />

                    <Route path="/student/orders" element={
                      <StudentRoute>
                        <MyOrders />
                      </StudentRoute>
                    } />
                    <Route path="/pay/request/:requestId" element={
                      <StudentRoute>
                        <PaymentRequestPage />
                      </StudentRoute>
                    } />
                    <Route path="/my-library" element={
                      <StudentRoute>
                        <StudentMyLibrary />
                      </StudentRoute>
                    } />
                    <Route path="/student/inbox" element={
                      <StudentRoute>
                        <StudentInboxPage />
                      </StudentRoute>
                    } />
                    <Route path="/student/study-time" element={
                      <StudentRoute>
                        <StudyTimePage />
                      </StudentRoute>
                    } />

                    {/* Fix for /student/passes and /student-dashboard link */}
                    <Route path="/student-dashboard" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/student/passes" element={<Navigate to="/student/orders" replace />} />
                    <Route path="/student/passes/:id" element={<Navigate to="/student/orders" replace />} />

                    <Route path="/library" element={
                      <ProtectedRoute>
                        <Library />
                      </ProtectedRoute>
                    } />

                    <Route path="/library/offline" element={
                      <ProtectedRoute>
                        <OfflineLibrary />
                      </ProtectedRoute>
                    } />

                    <Route path="/library/:username" element={
                      <ProtectedRoute>
                        <LibraryProfile />
                      </ProtectedRoute>
                    } />

                    <Route path="/resource/:id" element={
                      <ProtectedRoute>
                        <ResourceDetail />
                      </ProtectedRoute>
                    } />

                    <Route path="/report" element={
                      <ProtectedRoute>
                        <ReportPage />
                      </ProtectedRoute>
                    } />

                    <Route path="/review" element={
                      <StudentRoute>
                        <Review />
                      </StudentRoute>
                    } />

                    <Route path="/profile" element={
                      <StudentRoute>
                        <Profile />
                      </StudentRoute>
                    } />

                  <Route path="/notifications" element={
                    <StudentRoute>
                      <NotificationsPage />
                    </StudentRoute>
                  } />

                  <Route path="/student/ai-plan" element={
                    <StudentRoute>
                      <FeatureAvailabilityRoute flagId="toofi_plans" fallbackPath="/dashboard">
                        <AiPlan />
                      </FeatureAvailabilityRoute>
                    </StudentRoute>
                  } />

                    {/* Static Pages */}
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/features" element={<Features />} />
                    <Route path="/toofi" element={
                      <FeatureAvailabilityRoute flagId="toofi" fallbackPath="/features">
                        <Toofi />
                      </FeatureAvailabilityRoute>
                    } />
                    <Route path="/offline-study" element={<OfflineStudy />} />
                    <Route path="/trusted-resources" element={<TrustedResources />} />
                    <Route path="/for-publishers" element={<ForPublishers />} />
                    <Route path="/pricing" element={
                      <FeatureAvailabilityRoute flagId="toofi_plans" fallbackPath="/features">
                        <Pricing />
                      </FeatureAvailabilityRoute>
                    } />
                    <Route path="/share/study-plan" element={<StudyPlanShare />} />
                    <Route path="/help/how-to-pay" element={<HowToPay />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/install" element={<InstallGuide />} />
                    <Route path="/install-guide" element={<Navigate to="/install" replace />} />
                    <Route path="/copyright" element={<Copyright />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/refund-policy" element={<RefundPolicy />} />
                    <Route path="/publisher-agreement" element={<PublisherAgreement />} />
                  </Route>

                  {/* PUBLISHER ROUTES */}
                  <Route element={<DashboardLayout />}>
                    <Route path="/publisher" element={<PublisherRoute><PublisherDashboard /></PublisherRoute>} />
                    <Route path="/publisher/library" element={<PublisherRoute><MyLibraryPage /></PublisherRoute>} />
                    <Route path="/publisher/submissions" element={<PublisherRoute><SubmissionsPage /></PublisherRoute>} />
                    <Route path="/publisher/requests" element={<PublisherRoute><AccessRequestsPage /></PublisherRoute>} />
                    <Route path="/publisher/profile" element={<PublisherRoute><PublisherProfile /></PublisherRoute>} />
                    <Route path="/publisher/statistics" element={<PublisherRoute><PublisherStatistics /></PublisherRoute>} />
                  </Route>

                  {/* NEW ADMIN LAYOUT WRAPPER (Unified with generic DashboardLayout) */}
                  <Route element={<AdminLayout />}>
                    <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                    <Route path="/admin/inbox" element={<AdminRoute><AdminInboxPage /></AdminRoute>} />
                    <Route path="/admin/analytics" element={<AdminRoute><StudentAnalyticsPage /></AdminRoute>} />
                    <Route path="/admin/feature-flags" element={<AdminRoute><FeatureFlagsPage /></AdminRoute>} />
                    <Route path="/admin/resources" element={<AdminRoute><AdminResources /></AdminRoute>} />
                    <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
                    <Route path="/admin/plans" element={<AdminRoute><AdminPlans /></AdminRoute>} />
                    <Route path="/admin/bundle-sales" element={<AdminRoute><AdminBundleSales /></AdminRoute>} />
                    <Route path="/admin/messages" element={<AdminRoute><AdminMessages /></AdminRoute>} />
                    <Route path="/admin/submissions" element={<AdminRoute><AdminSubmissionsPage /></AdminRoute>} />
                    <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
                    <Route path="/admin/logs" element={<AdminRoute>< AdminLogs /></AdminRoute>} />
                    <Route path="/admin/announcements" element={<AdminRoute><AdminAnnouncements /></AdminRoute>} />
                    <Route path="/admin/statistics" element={<AdminRoute><AdminStatistics /></AdminRoute>} />
                    <Route path="/admin/ai-plan-orders" element={<AdminRoute><AdminPlanOrders /></AdminRoute>} />


                  </Route>

                  <Route element={<DashboardLayout />}>
                    <Route path="*" element={<NotFound />} />
                  </Route>

              </Routes>
              </Suspense>
              </ErrorBoundary>
            </WebMCPProvider>
              </BrowserRouter>
              </AuthProvider>    </QueryClientProvider>
  );
}

export default App;
